/**
 * The ONE place a `user.role` value maps to a capability.
 *
 * Four tiers, one column (`user.role`), no second axis:
 *
 * | role             | can do                                          |
 * | ---------------- | ----------------------------------------------- |
 * | `admin`          | view + edit everything, incl. user management    |
 * | `admin_readonly` | view everything; edit nothing                    |
 * | `manager`        | their direct reports, plus everything `user` has |
 * | `user`           | themselves; the few self-editable profile fields |
 *
 * **Why `admin_readonly` is a role and not a `canEdit` flag on `admin`:** Better
 * Auth grants its admin API (`createUser`, `setRole`, `removeUser`, ban/unban)
 * by role name via `adminRoles`. Keeping that at `["admin"]` means a read-only
 * admin cannot reach account management *by construction*. A boolean flag would
 * leave every one of those routes needing to be hand-gated, and a single miss
 * hands out control of accounts.
 *
 * Every predicate takes a plain `string | null | undefined` rather than a typed
 * union, because these values arrive from the database and from Better Auth
 * sessions: a legacy value, a typo, or a role introduced by a migration newer
 * than this build must all resolve to *least* privilege rather than throwing or
 * accidentally matching. Comparison is exact and case-sensitive — "Admin" is
 * not `admin`.
 */

const ADMIN = "admin";
const ADMIN_READONLY = "admin_readonly";
const MANAGER = "manager";

/** Can change anything in the app. */
export function canEditAll(role: string | null | undefined): boolean {
  return role === ADMIN;
}

/**
 * Can see anything in the app, including the admin-only surfaces (audit log,
 * error log, every profile). Says nothing about editing — pair it with
 * `canEditAll` on any mutation.
 */
export function canViewAll(role: string | null | undefined): boolean {
  return role === ADMIN || role === ADMIN_READONLY;
}

/**
 * Can create, edit, ban or delete user accounts. Deliberately narrower than
 * `canViewAll` — see the note above about Better Auth's `adminRoles`.
 */
export function canManageUsers(role: string | null | undefined): boolean {
  return role === ADMIN;
}

/**
 * Whether this person gets the manager surfaces (Team page, team certifications,
 * direct-report aspirations) for their own reports.
 *
 * **Both gates are required:** the `manager` role AND at least one direct
 * report. Having reports alone no longer unlocks anything — that is the point of
 * introducing the role. Holding the role with no reports shows the nav with an
 * explicit empty state rather than hiding it, so the reason is visible.
 *
 * Admins are included when they have reports: role is single-valued, so an admin
 * who manages people cannot also hold `manager`, and without this they would
 * lose their own Team page. It is still gated on actually having reports —
 * "my team" means people who report to you, and admins reach everyone else
 * through the admin surfaces.
 */
export function managerScope(role: string | null | undefined, hasDirectReports: boolean): boolean {
  if (!hasDirectReports) return false;
  return role === MANAGER || canViewAll(role);
}

/**
 * Whether to render the "My Team" nav item.
 *
 * Deliberately WIDER than `managerScope`, for two different reasons:
 *
 *   - A **manager** with no direct reports still sees it, and the page's own
 *     empty state tells them nobody reports to them. Hiding it would leave them
 *     unable to tell a missing permission from an empty team.
 *   - An **admin** sees it whether or not they have reports, because the report
 *     carries a company-wide scope only they can use (WI-307). This reversed the
 *     previous rule, which hid it from an admin managing nobody on the grounds
 *     that "my team" means people who report to you — true of the default view,
 *     but it put the only route to the company report behind having reports,
 *     which has nothing to do with it.
 *
 * `hasDirectReports` is now unused. It is kept in the signature because the
 * caller has it to hand and the distinction still exists in `managerScope`
 * below — dropping the parameter would invite someone to reach for
 * `showsTeamNav` where they meant that one.
 */
export function showsTeamNav(role: string | null | undefined, _hasDirectReports: boolean): boolean {
  return role === MANAGER || canViewAll(role);
}
